import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, ZoomIn, ZoomOut, Maximize2, Copy, Check, ChevronLeft, ChevronRight, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import { downloadInlineImage } from '../utils/inlineImages';
import RedactionCanvas from './RedactionCanvas';

const MIN_SCALE = 1;
const MAX_SCALE = 6;

// Convert any image src to a PNG blob (needed for clipboard write).
const toPngBlob = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
    };
    img.onerror = reject;
    img.src = src;
  });

// Full-screen image viewer with zoom, pan (clamped), copy, download and
// prev/next navigation across a ticket's embedded images.
// Rendered via a portal to <body> with explicit pointer-events so it stays
// interactive even when opened from inside a Radix Dialog.
export const ImageLightbox = ({ images, startIndex = 0, onClose }) => {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [copied, setCopied] = useState(false);
  const [redact, setRedact] = useState(false);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const touchRef = useRef({ mode: null, startDist: 0, startScale: 1, startX: 0, startY: 0, originX: 0, originY: 0, swipeX: 0 });
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const hasImages = list.length > 0;
  const current = hasImages ? list[Math.min(index, list.length - 1)] : null;

  const resetTransform = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Jump to the clicked image when (re)opened.
  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex, images]);

  // Reset zoom/pan whenever the shown image changes.
  useEffect(() => {
    resetTransform();
    setCopied(false);
    setRedact(false);
  }, [index, resetTransform]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + list.length) % list.length);
  }, [list.length]);
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % list.length);
  }, [list.length]);

  useEffect(() => {
    if (!hasImages) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && list.length > 1) goPrev();
      else if (e.key === 'ArrowRight' && list.length > 1) goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasImages, list.length, onClose, goPrev, goNext]);

  // Clamp pan offset so the image can't be dragged fully off-screen.
  const clampOffset = useCallback((x, y, s) => {
    const img = imgRef.current;
    const cont = containerRef.current;
    if (!img || !cont) return { x, y };
    const scaledW = img.offsetWidth * s;
    const scaledH = img.offsetHeight * s;
    const slack = 40; // small overscroll so edges stay reachable
    const maxX = Math.max(0, (scaledW - cont.clientWidth) / 2) + slack;
    const maxY = Math.max(0, (scaledH - cont.clientHeight) / 2) + slack;
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  // Non-passive wheel listener so we can preventDefault while zooming.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !hasImages) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((prev) => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev - e.deltaY * 0.0015 * prev));
        setOffset((o) => (next <= MIN_SCALE ? { x: 0, y: 0 } : clampOffset(o.x, o.y, next)));
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [hasImages, index, clampOffset]);

  if (!hasImages || !current) return null;

  const src = current.src;
  const isData = src.startsWith('data:image');
  const ext = (/^data:image\/([^;]+)/.exec(src)?.[1] || 'png').replace('jpeg', 'jpg');

  const zoomIn = () =>
    setScale((s) => {
      const next = Math.min(MAX_SCALE, +(s + 0.5).toFixed(2));
      setOffset((o) => clampOffset(o.x, o.y, next));
      return next;
    });
  const zoomOut = () =>
    setScale((s) => {
      const next = Math.max(MIN_SCALE, +(s - 0.5).toFixed(2));
      setOffset((o) => (next <= MIN_SCALE ? { x: 0, y: 0 } : clampOffset(o.x, o.y, next)));
      return next;
    });

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    const nx = dragRef.current.originX + (e.clientX - dragRef.current.startX);
    const ny = dragRef.current.originY + (e.clientY - dragRef.current.startY);
    setOffset(clampOffset(nx, ny, scale));
  };
  const endDrag = () => {
    dragRef.current.dragging = false;
  };

  // ── Touch: pinch-to-zoom, drag-to-pan, swipe-to-navigate ──
  const touchDist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      touchRef.current = { ...touchRef.current, mode: 'pinch', startDist: touchDist(e.touches[0], e.touches[1]), startScale: scale };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (scale > 1) {
        touchRef.current = { ...touchRef.current, mode: 'pan', startX: t.clientX, startY: t.clientY, originX: offset.x, originY: offset.y };
      } else {
        touchRef.current = { ...touchRef.current, mode: 'swipe', startX: t.clientX, swipeX: 0 };
      }
    }
  };
  const onTouchMove = (e) => {
    const tr = touchRef.current;
    if (tr.mode === 'pinch' && e.touches.length === 2) {
      const d = touchDist(e.touches[0], e.touches[1]);
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, tr.startScale * (d / (tr.startDist || 1))));
      setScale(next);
      setOffset((o) => (next <= MIN_SCALE ? { x: 0, y: 0 } : clampOffset(o.x, o.y, next)));
    } else if (tr.mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      setOffset(clampOffset(tr.originX + (t.clientX - tr.startX), tr.originY + (t.clientY - tr.startY), scale));
    } else if (tr.mode === 'swipe' && e.touches.length === 1) {
      tr.swipeX = e.touches[0].clientX - tr.startX;
    }
  };
  const onTouchEnd = () => {
    const tr = touchRef.current;
    if (tr.mode === 'swipe' && list.length > 1) {
      if (tr.swipeX > 60) goPrev();
      else if (tr.swipeX < -60) goNext();
    }
    touchRef.current = { ...tr, mode: null, swipeX: 0 };
  };

  const copyImage = async () => {
    try {
      if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
        throw new Error('unsupported');
      }
      let blob;
      try {
        const resp = await fetch(src);
        blob = await resp.blob();
      } catch {
        blob = null;
      }
      if (!blob || blob.type !== 'image/png') {
        blob = await toPngBlob(src);
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      toast.success('Image copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const msg = e && e.name === 'NotAllowedError'
        ? 'Clipboard permission denied by the browser'
        : 'Copy to clipboard is not supported in this browser';
      toast.error(msg);
    }
  };

  const btnClass = 'rounded-full bg-white/25 hover:bg-white/40 text-white p-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  const navBtnClass = 'absolute top-1/2 -translate-y-1/2 rounded-full bg-white/20 hover:bg-white/40 text-white p-3 transition-colors';

  const overlay = (
    <div
      data-testid="image-lightbox"
      onClick={onClose}
      style={{ pointerEvents: 'auto' }}
      className="co-scope fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
    >
      {/* Toolbar */}
      <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {!redact && (
          <>
            <button type="button" data-testid="lightbox-zoom-out-btn" title="Zoom out" onClick={zoomOut} disabled={scale <= MIN_SCALE} className={btnClass}>
              <ZoomOut className="h-5 w-5" />
            </button>
            <span data-testid="lightbox-zoom-level" className="text-white/80 text-xs tabular-nums w-10 text-center select-none">
              {Math.round(scale * 100)}%
            </span>
            <button type="button" data-testid="lightbox-zoom-in-btn" title="Zoom in" onClick={zoomIn} disabled={scale >= MAX_SCALE} className={btnClass}>
              <ZoomIn className="h-5 w-5" />
            </button>
            <button type="button" data-testid="lightbox-reset-btn" title="Reset zoom" onClick={resetTransform} disabled={scale === 1 && offset.x === 0 && offset.y === 0} className={btnClass}>
              <Maximize2 className="h-5 w-5" />
            </button>
            <button type="button" data-testid="lightbox-copy-btn" title="Copy image to clipboard" onClick={copyImage} className={btnClass}>
              {copied ? <Check className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
            </button>
          </>
        )}
        <button type="button" data-testid="lightbox-redact-btn" title="Redact (black out) sensitive areas" onClick={() => setRedact((v) => !v)} className={`${btnClass} ${redact ? 'bg-white/40' : ''}`}>
          <Scissors className="h-5 w-5" />
        </button>
        {!redact && isData && (
          <button type="button" data-testid="lightbox-download-btn" title="Download image" onClick={() => downloadInlineImage(src, current.name || `embedded-image-${Date.now()}.${ext}`)} className={btnClass}>
            <Download className="h-5 w-5" />
          </button>
        )}
        <button type="button" data-testid="lightbox-close-btn" title="Close" onClick={onClose} className={btnClass}>
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Prev / Next navigation */}
      {!redact && list.length > 1 && (
        <>
          <button type="button" data-testid="lightbox-prev-btn" title="Previous image" onClick={(e) => { e.stopPropagation(); goPrev(); }} className={`${navBtnClass} left-4`}>
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button type="button" data-testid="lightbox-next-btn" title="Next image" onClick={(e) => { e.stopPropagation(); goNext(); }} className={`${navBtnClass} right-4`}>
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {redact ? (
        <RedactionCanvas src={src} onExit={() => setRedact(false)} />
      ) : (
      <>
      {/* Image stage */}
      <div
        ref={containerRef}
        className="flex items-center justify-center w-full h-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ cursor: scale > 1 ? (dragRef.current.dragging ? 'grabbing' : 'grab') : 'default', touchAction: 'none' }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={current.name || 'Full size'}
          draggable={false}
          className="max-h-[80vh] max-w-[92vw] object-contain rounded-md shadow-2xl bg-white select-none"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: dragRef.current.dragging ? 'none' : 'transform 0.12s ease-out',
          }}
        />
      </div>

      {/* Bottom area: thumbstrip + caption/counter */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 w-full px-4" onClick={(e) => e.stopPropagation()}>
        {list.length > 1 && (
          <div data-testid="lightbox-thumbstrip" className="flex gap-2 max-w-[92vw] overflow-x-auto py-1">
            {list.map((im, i) => (
              <button
                key={i}
                type="button"
                data-testid="lightbox-thumb"
                title={im.name}
                onClick={() => setIndex(i)}
                className={`h-11 w-11 rounded border-2 overflow-hidden shrink-0 transition-opacity ${i === index ? 'border-white' : 'border-white/30 opacity-60 hover:opacity-100'}`}
              >
                <img src={im.src} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <span data-testid="lightbox-caption" className="text-white/90 text-sm max-w-[80vw] truncate select-none">
          {current.name}
          {list.length > 1 && <span className="text-white/50"> ({index + 1}/{list.length})</span>}
        </span>
        <span className="text-white/40 text-xs select-none hidden sm:block">Scroll/pinch to zoom · drag to pan · ← → / swipe to browse · Esc to close</span>
      </div>
      </>
      )}
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default ImageLightbox;
